import { useEffect, useState } from 'react';
import { Play, Loader2, Clock } from 'lucide-react';
import { apiFetch, getApiBase } from './apiBase';
import ReconSection from './ReconSection';
import FileUploadField from './FileUploadField';
import HelpTip from './HelpTip';
import { RECON_VISIBLE_KEY } from './reconShared';
import {
  getReconSettings,
  formatReconSettingsSummary,
  RECON_SETTINGS_EVENT,
} from './reconSettings';
import {
  RECON_SCOPE_OPTIONS,
  DEFAULT_RECON_SCOPE,
  scopeUsesDollars,
  scopeUsesHours,
} from './reconScopes';

export default function PayrollRecon({ onRunComplete, onNavigate }) {
  const [reconScope, setReconScope] = useState(
    () => localStorage.getItem('reconScope') || DEFAULT_RECON_SCOPE
  );
  const [scopeStats, setScopeStats] = useState(null);
  const [settings, setSettings] = useState(getReconSettings);
  const [oracleFile, setOracleFile] = useState(null);
  const [registerFile, setRegisterFile] = useState(null);
  const [g2File, setG2File] = useState(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState('');
  const [runSuccess, setRunSuccess] = useState(false);
  const [summary, setSummary] = useState(null);
  const [docsInputs, setDocsInputs] = useState(null);

  useEffect(() => {
    apiFetch('/api/docs/inputs')
      .then(setDocsInputs)
      .catch(() => setDocsInputs(null));
  }, []);

  const loadSummaryIfAllowed = async () => {
    if (sessionStorage.getItem(RECON_VISIBLE_KEY) !== '1') return;
    try {
      const { summary: s } = await apiFetch('/api/recon/summary');
      if (s?.reconMode === 'element-wage-type' || s?.grain?.includes('ORACLE_ELEMENT')) {
        setSummary(s);
      }
    } catch (_) {
      setSummary(null);
    }
  };

  useEffect(() => {
    loadSummaryIfAllowed();
  }, []);

  useEffect(() => {
    apiFetch('/api/recon/scopes')
      .then((d) => setScopeStats(d.scopes || null))
      .catch(() => setScopeStats(null));
  }, []);

  useEffect(() => {
    const onSettings = () => setSettings(getReconSettings());
    window.addEventListener(RECON_SETTINGS_EVENT, onSettings);
    return () => window.removeEventListener(RECON_SETTINGS_EVENT, onSettings);
  }, []);

  const runRecon = async () => {
    const runSettings = getReconSettings();
    setError('');
    setRunSuccess(false);
    setRunning(true);
    sessionStorage.removeItem(RECON_VISIBLE_KEY);
    try {
      const fd = new FormData();
      fd.append('payPeriod', runSettings.payPeriod);
      fd.append('reconScope', reconScope);
      fd.append('toleranceAmount', runSettings.toleranceAmount);
      fd.append('tolerancePercent', runSettings.tolerancePercent);
      fd.append('hoursToleranceAmount', runSettings.hoursToleranceAmount);
      localStorage.setItem('reconScope', reconScope);
      if (oracleFile) fd.append('elementEntries', oracleFile);
      if (registerFile) fd.append('payrollRegister', registerFile);
      if (g2File) fd.append('g2TimeFile', g2File);

      const base = getApiBase();
      const res = await fetch(`${base}/api/recon/run`, {
        method: 'POST',
        credentials: 'include',
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Recon failed');

      setSummary(data.summary);
      sessionStorage.setItem(RECON_VISIBLE_KEY, '1');
      setRunSuccess(true);
      onRunComplete?.();
    } catch (e) {
      setError(e.message);
    } finally {
      setRunning(false);
    }
  };

  const validation = summary?.validation || {};
  const usesDollars = scopeUsesDollars(reconScope);
  const usesHours = scopeUsesHours(reconScope);
  const scopeLabel =
    RECON_SCOPE_OPTIONS.find((o) => o.id === reconScope)?.label ||
    summary?.reconScopeLabel ||
    reconScope;
  const runLabel = summary?.runAt
    ? new Date(summary.runAt).toLocaleString(undefined, {
        dateStyle: 'medium',
        timeStyle: 'short',
      })
    : null;

  return (
    <div className="space-y-6">
      <header className="page-hero">
        <div className="max-w-2xl">
          <h2 className="text-2xl font-bold tracking-tight text-slate-100">
            Payroll reconciliation
          </h2>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-slate-400">
            Compare Oracle element entries against the ADP payroll register. Pull Oracle data on{' '}
            <button type="button" onClick={() => onNavigate?.('reports')} className="link-accent">
              Reports
            </button>
            , add the cycle on{' '}
            <button type="button" onClick={() => onNavigate?.('periods')} className="link-accent">
              Payroll periods
            </button>
            , then review output on{' '}
            <button type="button" onClick={() => onNavigate?.('results')} className="link-accent">
              Results
            </button>
            .
          </p>
          {summary && (
            <div className="mt-4 flex flex-wrap gap-2">
              <span className="badge-success">
                Last run · period {summary.payPeriod || settings.payPeriod}
              </span>
              {(summary.reconScopeLabel || summary.reconScope) && (
                <span className="badge-neutral">{summary.reconScopeLabel || summary.reconScope}</span>
              )}
              {runLabel && (
                <span className="badge-neutral inline-flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  {runLabel}
                </span>
              )}
            </div>
          )}
        </div>
      </header>

      {runSuccess && (
        <div className="alert-success">
          Reconciliation finished with {summary?.exceptionCount?.toLocaleString() ?? '—'} exceptions.
          Open <strong>Results</strong> in the sidebar for full metrics and drill-down.
        </div>
      )}

      <ReconSection
        title="Run configuration"
        headerCenter={
          <button type="button" onClick={runRecon} disabled={running} className="btn-primary">
            {running ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Play className="h-4 w-4" />
            )}
            Run reconciliation
          </button>
        }
        headerExtra={
          <p className="text-right text-xs leading-relaxed text-slate-400">
            {scopeLabel} · {formatReconSettingsSummary(settings)} ·{' '}
            <button type="button" onClick={() => onNavigate?.('periods')} className="link-accent">
              change period
            </button>
          </p>
        }
      >
        <fieldset className="mb-5">
          <legend className="text-sm font-medium text-slate-300">Reconciliation scope</legend>
          <div className="mt-3 grid gap-2 sm:grid-cols-1">
            {RECON_SCOPE_OPTIONS.map((opt) => {
              const stats = scopeStats?.[opt.id];
              const selected = reconScope === opt.id;
              return (
                <label
                  key={opt.id}
                  className={`scope-option ${selected ? 'scope-option-selected' : ''}`}
                >
                  <input
                    type="radio"
                    name="reconScope"
                    value={opt.id}
                    checked={selected}
                    onChange={() => setReconScope(opt.id)}
                    className="mt-1 shrink-0"
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-medium text-slate-100">{opt.label}</span>
                    <span className="block text-xs text-slate-500">{opt.hint}</span>
                    {stats && (
                      <span className="mt-1 block font-mono text-[10px] text-slate-400">
                        {stats.mappedElements} elements · {stats.mappedWageTypes} wage types in scope
                      </span>
                    )}
                  </span>
                </label>
              );
            })}
          </div>
        </fieldset>

        {validation.blocker && summary?.reconScope === reconScope && (
          <div className="alert-warning mb-4">
            <p className="font-semibold">Cannot run {scopeLabel}</p>
            <p className="mt-1">{validation.blocker}</p>
            {!usesHours && (
              <p className="mt-1 text-xs opacity-90">
                {validation.elementNamePopulated ?? 0} of {validation.oracleRows ?? '?'} rows have
                an element name — add the element name column to the Oracle report, or run
                Element Entries on <strong>Reports</strong>.
              </p>
            )}
          </div>
        )}

        <div className="grid gap-3">
          <FileUploadField
            label="Oracle element entries"
            hint="Upload to override the saved file from Reports"
            accept=".xlsx,.xls"
            file={oracleFile}
            onChange={setOracleFile}
            savedFile={docsInputs?.elementEntries}
            helpTerm="element"
            HelpTipComponent={HelpTip}
          />
          <FileUploadField
            label="ADP payroll register"
            hint="Register with Amount column — or use the saved file from Reports"
            accept=".xlsx,.xls"
            file={registerFile}
            onChange={setRegisterFile}
            savedFile={docsInputs?.payrollRegister}
            helpTerm="register"
            HelpTipComponent={HelpTip}
          />
          <FileUploadField
            label="ADP G2 time file"
            hint={
              usesHours
                ? 'Required for time recon — or use the saved G2 file'
                : 'Not used for this scope (choose Time recon to compare hours)'
            }
            accept=".txt"
            file={g2File}
            onChange={setG2File}
            savedFile={docsInputs?.g2TimeFile}
            optional={!usesHours}
            helpTerm="g2"
            HelpTipComponent={HelpTip}
          />
        </div>

        {error && <div className="alert-error mt-4">{error}</div>}
      </ReconSection>
    </div>
  );
}
